import React, { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { logger } from "@/lib/logger";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null=checking, false=unauth, object=auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/api/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      // Cache the last signed-in user so the Login page can greet them by
      // name + avatar next time. Stripped to public fields only.
      try {
        localStorage.setItem("last_login_user", JSON.stringify({
          name: data.name, email: data.email, avatar_url: data.avatar_url || null,
        }));
      } catch { /* localStorage full or disabled — non-critical */ }
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/api/auth/logout"); }
    catch (e) { logger.warn("logout endpoint failed — clearing local session anyway", e); }
    localStorage.removeItem("access_token");
    localStorage.removeItem("last_login_user");
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);

export const hasRole = (user, ...roles) => user && roles.includes(user.role);
